/**
 * Probability density functions for the continuous distributions the charts draw.
 *
 * `distributions.ts` has every CDF, tail and quantile but only the normal density,
 * because a test result never needs the height of a curve. A sketch of the curve with
 * its tail shaded does, so the Student t, chi-square and F densities live here — closed
 * forms evaluated in log space through `logGamma`/`logBeta`, so large degrees of freedom
 * do not overflow. Pinned against `scipy.stats` in `stats-distributions.test.ts`.
 */

import { logBeta, logGamma } from "@/lib/stats/special";

/** Density of Student's t with `df` degrees of freedom (df may be fractional). */
export function studentTPdf(t: number, df: number): number {
  return Math.exp(
    logGamma((df + 1) / 2) -
      logGamma(df / 2) -
      0.5 * Math.log(df * Math.PI) -
      ((df + 1) / 2) * Math.log1p((t * t) / df)
  );
}

/** Density of the chi-square distribution. Infinite at 0 when df < 2. */
export function chiSquarePdf(x: number, df: number): number {
  if (x < 0) {
    return 0;
  }
  if (x === 0) {
    if (df < 2) {
      return Number.POSITIVE_INFINITY;
    }
    return df === 2 ? 0.5 : 0;
  }
  const k = df / 2;
  return Math.exp((k - 1) * Math.log(x) - x / 2 - k * Math.LN2 - logGamma(k));
}

/** Density of the F distribution with `d1` and `d2` degrees of freedom. */
export function fPdf(x: number, d1: number, d2: number): number {
  if (x < 0) {
    return 0;
  }
  if (x === 0) {
    if (d1 < 2) {
      return Number.POSITIVE_INFINITY;
    }
    return d1 === 2 ? 1 : 0;
  }
  return Math.exp(
    0.5 * (d1 * Math.log(d1 * x) + d2 * Math.log(d2)) -
      0.5 * (d1 + d2) * Math.log(d1 * x + d2) -
      Math.log(x) -
      logBeta(d1 / 2, d2 / 2)
  );
}
